import { useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, Search, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";

export const PhoneImportDialog = ({ open, stations, isAdmin, defaultStation, onClose, onDone }) => {
  const [file, setFile] = useState(null);
  const [station, setStation] = useState(defaultStation || "");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const send = async (confirm) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("station_id", station || "");
    fd.append("confirm", confirm);
    setBusy(true);
    try {
      const { data } = await api.post("/phones/import", fd);
      if (confirm) { toast.success(`Importati ${data.count} telefoni (${data.new} nuovi, ${data.update} aggiornati)`); onDone(); }
      else setPreview(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="phone-import-dialog">
        <DialogHeader>
          <DialogTitle>Caricamento massivo telefoni</DialogTitle>
          <DialogDescription>Colonna obbligatoria: IMEI TELEFONO. Facoltative: NUMERO INTERNO, MARCA, MODELLO, IMEI SIM, NUMERO TELEFONO, ASSEGNATARIO (diventa assegnatario fisso, la firma si raccoglie dopo), NOTE{isAdmin && ", STAZIONE"}. I telefoni già presenti (stesso IMEI) vengono aggiornati.</DialogDescription>
        </DialogHeader>
        {isAdmin && (
          <div className="space-y-1.5">
            <Label>Stazione</Label>
            <Select value={station || undefined} onValueChange={(v) => { setStation(v); setPreview(null); }}>
              <SelectTrigger data-testid="phone-import-station-select"><SelectValue placeholder="Seleziona stazione" /></SelectTrigger>
              <SelectContent>{stations.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        <label className="flex h-10 cursor-pointer items-center gap-2 rounded-md border border-dashed border-slate-300 px-3 text-sm text-slate-600 hover:border-slate-400">
          <FileSpreadsheet className="h-4 w-4 text-emerald-600" /><span className="truncate">{file ? file.name : "Scegli file Excel (.xlsx)..."}</span>
          <input type="file" accept=".xlsx" className="hidden" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }} data-testid="phone-import-file-input" />
        </label>
        <Button onClick={() => send(false)} disabled={!file || busy || (isAdmin && !station)} data-testid="phone-import-preview-button"><Search className="mr-2 h-4 w-4" /> Analizza file</Button>
        {preview && (
          <div className="space-y-3 rounded-lg border p-4 text-sm" data-testid="phone-import-preview">
            <p><b data-testid="phone-import-count">{preview.count}</b> telefoni validi ({preview.new} nuovi, {preview.update} da aggiornare) · {preview.assigned} con assegnatario</p>
            <p className="text-xs text-slate-500">{preview.per_station.map((s) => `${s.station_name}: ${s.count}`).join(" · ")}</p>
            {[["warnings", "avvisi (righe importate comunque)", "bg-amber-50 text-amber-800", "warning"], ["errors", "righe scartate", "bg-rose-50 text-rose-700", "error"]].map(([k, t, cls, f]) => preview[k].length > 0 && (
              <div key={k} className={`max-h-40 overflow-auto rounded-md p-2 text-xs ${cls}`} data-testid={`phone-import-${k}`}>
                <p className="mb-1 font-semibold">{preview[k].length} {t}:</p>
                {preview[k].map((w) => <p key={`${w.row}-${w.imei}`}>Riga {w.row} ({w.imei}): {w[f]}</p>)}
              </div>
            ))}
            <Button onClick={() => send(true)} disabled={busy || !preview.count} data-testid="phone-import-confirm-button"><Upload className="mr-2 h-4 w-4" /> Importa</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
